import { useState, useEffect } from 'react';
import { useModal } from '@components/ModalContext';
import SHDButton from '@ui/Button/SHDButton';
import SHDCallout from '@ui/Callout/SHDCallout';
import SHDText from '@ui/Text/SHDText';
import SHDThemeOverride from '@ui/ThemeOverride/SHDThemeOverride';
import { Box } from 'grommet';
import { ScoreSettingsProps, ScoringPairEvents } from './scoreSettings.d';
import ScorePairingFlow from './ScorePairingFlow';
import useIScore from '@hooks/useIScore';
import SHDLoader from '@ui/Loader/SHDLoader';
import Toast from '@components/Toast';
import useEventsGameStart from '@hooks/useEventsGameStart';

const ScoringSettings: React.FC<ScoreSettingsProps> = ({
  teamId,
  teamHandle,
  teamPin = '00000',
  sportType,
}) => {
  const { showModal, hideModal } = useModal();
  const {
    unpairFromIScore,
    checkIScoreStatus,
    synced,
    isSyncing,
    syncIScoreRoster,
  } = useIScore(teamId);
  const [showToast, setShowToast] = useState<boolean>(false);
  const [toastLabel, setToastLabel] = useState<string>('');
  const { trackGameStartEvent } = useEventsGameStart(teamId, sportType);

  const checkSyncStatus = async () => {
    await checkIScoreStatus();
  };

  useEffect(() => {
    checkSyncStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const renderModal = () => {
    trackGameStartEvent(ScoringPairEvents.PAIR_ISCORE_CLICKED);
    showModal(
      <ScorePairingFlow
        onClose={() => {
          hideModal();
          checkSyncStatus();
        }}
        teamId={teamId}
        teamHandle={teamHandle}
        teamPin={teamPin}
      />,
      {
        onClose: () => hideModal(),
        className: 'is-transparent-controls',
      }
    );
  };

  const handleUnpair = () => {
    trackGameStartEvent(ScoringPairEvents.UNPAIR_ISCORE_CLICKED);
    unpairFromIScore();
  };

  const reSyncRoster = async () => {
    trackGameStartEvent(ScoringPairEvents.ISCORE_SCYNC_CLICKED);
    const success = await syncIScoreRoster();
    if (success) {
      setToastLabel('Roster re-sync successful');
    } else {
      setToastLabel('Roster re-sync failed, please try again.');
    }
    setShowToast(true);
  };

  return (
    <SHDThemeOverride background="gray-50" full={false}>
      <Box margin={{ vertical: 'medium', horizontal: 'small' }}>
        <SHDCallout background="white" round="medium" gap="xxsmall">
          <Box gap="xxsmall" width={'100%'} margin={{ vertical: 'small' }}>
            <SHDText style="subhead" color="black">
              iScore
            </SHDText>

            {isSyncing ? (
              <Box alignSelf="center">
                <SHDLoader />
              </Box>
            ) : synced ? (
              <Box gap="xxsmall">
                <Box gap="medium" margin={{ top: 'xsmall', bottom: 'medium' }}>
                  <Box direction="row" justify="between">
                    <SHDText style="subhead">Team handle</SHDText>
                    <Box direction="row">
                      <SHDText style="subhead" color="gray-700">
                        {teamHandle}
                      </SHDText>
                    </Box>
                  </Box>
                  <Box direction="row" justify="between">
                    <SHDText style="subhead">Team PIN</SHDText>
                    <Box direction="row">
                      <SHDText style="subhead" color="gray-700">
                        {teamPin}
                      </SHDText>
                    </Box>
                  </Box>
                </Box>
                <SHDButton
                  secondary
                  onClick={reSyncRoster}
                  label="Re-sync roster"
                />
                <SHDButton
                  warning
                  onClick={handleUnpair}
                  label="Unpair iScore"
                />
              </Box>
            ) : (
              // Show unpaired UI
              <Box gap="xxlarge">
                <SHDText style="body">
                  iScore is a separate scorekeeping app that pairs with
                  sidelineHD. Control scoreboard overlays, get spray charts and
                  see player stats.
                </SHDText>

                <SHDButton
                  secondary
                  onClick={renderModal}
                  label="Pair iScore"
                />
              </Box>
            )}
          </Box>
        </SHDCallout>
        {showToast && (
          <Toast
            label={toastLabel}
            background="status-ok"
            duration={3000}
            onClose={() => {
              setShowToast(false);
            }}
          />
        )}
      </Box>
    </SHDThemeOverride>
  );
};

export default ScoringSettings;
